/**
 * Pure geometry for the /projects city board. Everything is expressed in tile
 * coordinates (i along the board's right-going edge, j along its left-going
 * edge, z up in screen pixels) and projected with a 2:1 isometric camera.
 */

export const TILE_W = 64;
export const TILE_H = 32;
/** Tiles per city block (square). */
export const BLOCK = 3;
/** Tiles per road. */
export const ROAD = 1;
/** Pixels per building storey. */
export const FLOOR_H = 11;

export type Pt = readonly [number, number];

export function iso(i: number, j: number, z = 0): Pt {
  return [(i - j) * (TILE_W / 2), (i + j) * (TILE_H / 2) - z];
}

export function points(pts: readonly Pt[]): string {
  return pts.map(([x, y]) => `${round(x)},${round(y)}`).join(' ');
}

export function pathD(pts: readonly Pt[], close = true): string {
  const [first, ...rest] = pts;
  if (!first) return '';
  return `M${round(first[0])},${round(first[1])}${rest.map(([x, y]) => `L${round(x)},${round(y)}`).join('')}${close ? 'Z' : ''}`;
}

function round(n: number): number {
  return Math.round(n * 10) / 10;
}

/** FNV-1a — stable 32-bit hash so a project always gets the same building. */
export function hash(input: string): number {
  let h = 0x811c9dc5;
  for (let k = 0; k < input.length; k++) {
    h ^= input.charCodeAt(k);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Mulberry32 — tiny seeded PRNG, returns floats in [0, 1). */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Storeys for a building: grows with team size, capped so the skyline stays readable. */
export function floorsFor(memberCount: number): number {
  return 2 + Math.min(Math.max(memberCount, 0), 10);
}

/** Height of a building's roof deck in px: a taller ground floor, then storeys. */
export function roofHeight(floors: number): number {
  return 15 + Math.max(floors - 1, 1) * FLOOR_H;
}

export type DecorKind = 'park' | 'plaza' | 'pond';

export type Block = {
  col: number;
  row: number;
  /** Tile origin of the block's back corner. */
  i0: number;
  j0: number;
  /** "A-01": row letter + column number, printed on the lot plaque. */
  code: string;
} & ({ kind: 'lot'; projectIndex: number } | { kind: 'decor'; decor: DecorKind; seed: number });

export type CityPlan = {
  cols: number;
  rows: number;
  /** Board extent in tiles, roads included. */
  width: number;
  height: number;
  blocks: Block[];
};

const DECOR_CYCLE: DecorKind[] = ['park', 'plaza', 'pond', 'park'];

/**
 * Lay out `projectCount` lots plus enough parks and plazas to read as a city.
 * Deterministic for a given count, so lot codes don't shuffle between visits.
 */
export function planCity(projectCount: number): CityPlan {
  const decorCount = Math.max(3, Math.ceil(projectCount * 0.35));
  const total = projectCount + decorCount;
  const cols = Math.max(3, Math.ceil(Math.sqrt(total)));
  const rows = Math.max(2, Math.ceil(total / cols));
  const cells = cols * rows;

  // Projects take the cells nearest the board's centre (front-weighted, since
  // the front reads first); parks and plazas ring them. A seeded jitter breaks
  // ties so the town isn't a perfect target, but the same count always yields
  // the same town and the same lot codes.
  const rand = rng(hash(`pmd-city-${cols}x${rows}`));
  const cc = (cols - 1) / 2;
  const rc = (rows - 1) / 2 + 0.35;
  const ranked = Array.from({ length: cells }, (_, k) => {
    const col = k % cols;
    const row = Math.floor(k / cols);
    return { k, d: Math.hypot(col - cc, row - rc) + rand() * 0.3 };
  }).sort((a, z) => a.d - z.d);
  const decorCells = new Set(ranked.slice(projectCount).map((r) => r.k));

  const blocks: Block[] = [];
  let projectIndex = 0;
  let decorIndex = 0;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const cell = row * cols + col;
      const base = {
        col,
        row,
        i0: ROAD + col * (BLOCK + ROAD),
        j0: ROAD + row * (BLOCK + ROAD),
        code: `${String.fromCharCode(65 + (row % 26))}-${String(col + 1).padStart(2, '0')}`,
      };
      if (decorCells.has(cell)) {
        blocks.push({
          ...base,
          kind: 'decor',
          decor: DECOR_CYCLE[decorIndex % DECOR_CYCLE.length]!,
          seed: hash(`decor-${cell}`),
        });
        decorIndex++;
      } else {
        blocks.push({ ...base, kind: 'lot', projectIndex });
        projectIndex++;
      }
    }
  }

  return {
    cols,
    rows,
    width: ROAD + cols * (BLOCK + ROAD),
    height: ROAD + rows * (BLOCK + ROAD),
    blocks,
  };
}

/** Centre-line dash and gap, in tiles. */
export const DASH = 0.2;
export const DASH_GAP = 0.2;

/**
 * Dashes filling [a, b] along a road, evenly spaced and centred so both ends
 * of the run look the same. Returns [start, end] pairs in tiles.
 */
export function dashRuns(a: number, b: number, dash = DASH, gap = DASH_GAP): [number, number][] {
  const len = b - a;
  if (len < dash) return [];
  const count = Math.floor((len + gap) / (dash + gap));
  const used = count * dash + (count - 1) * gap;
  const start = a + (len - used) / 2;
  return Array.from({ length: count }, (_, n) => {
    const t = start + n * (dash + gap);
    return [t, t + dash] as [number, number];
  });
}

/**
 * Which intersections get pedestrian crossings, as `"q,r"` keys on the road
 * grid (q = 0…cols, r = 0…rows). Every junction qualifies: inner crossroads,
 * the T-junctions on the ring road, and the four board corners. About half are
 * picked, seeded by the board size so the same town always paints the same
 * crossings, and at least one is kept.
 */
export function crossingsFor(plan: Pick<CityPlan, 'cols' | 'rows'>): Set<string> {
  const rand = rng(hash(`pmd-crossings-${plan.cols}x${plan.rows}`));
  const junctions: string[] = [];
  const picked = new Set<string>();
  for (let r = 0; r <= plan.rows; r++) {
    for (let q = 0; q <= plan.cols; q++) {
      const key = `${q},${r}`;
      junctions.push(key);
      if (rand() < 0.45) picked.add(key);
    }
  }
  if (picked.size === 0 && junctions.length > 0) {
    picked.add(junctions[Math.floor(rand() * junctions.length)]!);
  }
  return picked;
}

export type Facing = 1 | -1;

export type Loop = {
  /** SVG path in screen space, closed. */
  d: string;
  /** Facing per leg (1 = walking toward screen-right), and where each leg starts as a fraction of the loop. */
  facing: Facing[];
  keyTimes: number[];
  /**
   * Per leg: true when it runs along the block's back edges (min i or min j),
   * where whatever stands on the block is between the pawn and the viewer.
   */
  back: boolean[];
};

/**
 * A rectangular walking loop in tile space. Going +i walks screen-right-down
 * and +j screen-left-down, so facing flips per leg; `reverse` walks the other way.
 */
export function loopPath(a: number, b: number, c: number, d: number, reverse = false): Loop {
  let corners: [number, number][] = [
    [a, b],
    [c, b],
    [c, d],
    [a, d],
  ];
  if (reverse) corners = [corners[0]!, corners[3]!, corners[2]!, corners[1]!];
  const legs = corners.map((p, k) => [p, corners[(k + 1) % 4]!] as const);
  const lengths = legs.map(([p, q]) => Math.abs(q[0] - p[0]) + Math.abs(q[1] - p[1]));
  const total = lengths.reduce((s, l) => s + l, 0);
  const keyTimes: number[] = [];
  let acc = 0;
  for (const l of lengths) {
    keyTimes.push(Math.round((acc / total) * 1000) / 1000);
    acc += l;
  }
  const facing = legs.map(([p, q]): Facing => {
    const screenDx = q[0] - p[0] - (q[1] - p[1]);
    return screenDx >= 0 ? 1 : -1;
  });
  const back = legs.map(([p, q]) => (p[0] === a && q[0] === a) || (p[1] === b && q[1] === b));
  return { d: pathD(corners.map(([i, j]) => iso(i, j))), facing, keyTimes, back };
}

let measureCtx: CanvasRenderingContext2D | null | undefined;

/** Width of a label in px, measured with the page font; falls back to an estimate under jsdom. */
export function textWidth(text: string, size: number, weight = 400): number {
  if (measureCtx === undefined) {
    try {
      measureCtx =
        typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
    } catch {
      measureCtx = null;
    }
  }
  if (!measureCtx) return text.length * size * 0.56;
  measureCtx.font = `${weight} ${size}px Inter, ui-sans-serif, system-ui, sans-serif`;
  return measureCtx.measureText(text).width;
}

/**
 * Break `text` into lines no wider than `max` px, on word boundaries. A single
 * word wider than `max` gets its own line rather than being cut: names are
 * never hidden.
 */
export function wrapText(text: string, max: number, size: number, weight = 400): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (line && textWidth(next, size, weight) > max) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** Cut `text` with an ellipsis so it fits `max` px. */
export function fitText(text: string, max: number, size: number, weight = 400): string {
  if (textWidth(text, size, weight) <= max) return text;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (textWidth(`${text.slice(0, mid).trimEnd()}…`, size, weight) <= max) lo = mid;
    else hi = mid - 1;
  }
  return `${text.slice(0, lo).trimEnd()}…`;
}

/** A sign to stand over a roof: centred on `anchor`, sized `w` x `h` in sign units, drawn at scale `k`. */
export type SignBox = { anchor: Pt; w: number; h: number; k: number };

const SIGN_GAP = 10;
/** Float slack in overlap tests: without it, rounding can leave a sign "overlapping" the one it just cleared. */
const SIGN_EPS = 0.01;

/**
 * Stand each sign above its own roof, front buildings first; a sign that
 * would overlap one already placed climbs above it on a taller post. `post`
 * is the gap left under each sign for its post, in sign units.
 *
 * Each climb lands the sign strictly above the one it cleared, so a sign moves
 * at most once per sign already placed; the loop is bounded to that.
 */
export function placeSigns<T extends SignBox>(
  post: number,
  items: T[],
): (T & { x: number; y: number })[] {
  const placed: (T & { x: number; y: number })[] = [];
  const order = [...items.keys()].sort((a, z) => items[z]!.anchor[1] - items[a]!.anchor[1]);
  const out: (T & { x: number; y: number })[] = [];
  for (const idx of order) {
    const it = items[idx]!;
    const w = it.w * it.k;
    const h = (it.h + post) * it.k;
    const x = it.anchor[0];
    let y = it.anchor[1] - 22 - h;
    let moved = true;
    for (let pass = 0; moved && pass <= placed.length; pass++) {
      moved = false;
      for (const p of placed) {
        const pw = p.w * p.k;
        const ph = (p.h + post) * p.k;
        const overlapX = Math.abs(p.x - x) < (pw + w) / 2 + SIGN_GAP;
        const overlapY = y < p.y + ph + SIGN_GAP - SIGN_EPS && y + h + SIGN_GAP > p.y + SIGN_EPS;
        if (overlapX && overlapY) {
          y = p.y - h - SIGN_GAP;
          moved = true;
        }
      }
    }
    const layout = { ...it, x, y };
    placed.push(layout);
    out[idx] = layout;
  }
  return out;
}

/** Map zoom bounds, relative to the board's fit-to-screen size. */
export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 2.5;

export function clampZoom(z: number): number {
  return Math.round(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z)) * 100) / 100;
}
